/**
 * Service for copying Encompass eFolder documents between loans
 *
 * IMPORTANT: This service uploads documents directly to Encompass.
 * Documents will appear in the destination loan's eFolder in Encompass,
 * then sync back to Supabase via webhooks automatically.
 */

import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('EncompassDocumentCopyService', 'encompass');

export interface CopyDocumentsRequest {
  attachmentIds: string[];
  destinationLoanId: string;
  /** Source loan (encompass_loan_id) — server-pins every attachment to it. */
  sourceLoanId: string;
}

export interface CopyDocumentsResult {
  success: boolean;
  copiedCount: number;
  failedCount: number;
  newAttachmentIds: string[];
  errors: string[];
}

/**
 * Copies selected documents from one loan to another via Encompass API
 *
 * Flow:
 * 1. Calls encompass-document-sync edge function with copyMode=true
 * 2. Edge function downloads files from Supabase Storage
 * 3. Edge function uploads files to Encompass API
 * 4. Documents appear in destination loan's eFolder (Unassigned)
 * 5. Existing webhooks sync them back to Supabase automatically
 */
export async function copyDocumentsBetweenLoans(
  request: CopyDocumentsRequest
): Promise<CopyDocumentsResult> {
  const result: CopyDocumentsResult = {
    success: true,
    copiedCount: 0,
    failedCount: 0,
    newAttachmentIds: [],
    errors: []
  };

  log.info('Starting document copy to Encompass', {
    attachmentCount: request.attachmentIds.length,
    destinationLoanId: request.destinationLoanId
  });

  try {
    // No client-side timeout race: the edge function streams the copy and is
    // bounded by its own wall-clock limit. A short client race produced false
    // "all failed" results on large copies while the server kept running — and
    // it could not abort that server work anyway.
    const { data, error } = await supabase.functions.invoke('encompass-document-sync', {
      body: {
        copyMode: true,
        attachmentIds: request.attachmentIds,
        destinationLoanId: request.destinationLoanId,
        // Server-pins every source attachment to this loan (defense in depth
        // on top of org enforcement).
        expectedSourceLoanId: request.sourceLoanId
      }
    });

    if (error) {
      // supabase-js FunctionsHttpError carries the raw Response on `.context`;
      // read its status so a resource-limit kill (HTTP 546) surfaces as an
      // actionable message instead of the generic "non-2xx status code".
      const status = readErrorStatus(error);
      log.error('Edge function invocation failed', { error, status });
      return {
        ...result,
        success: false,
        failedCount: request.attachmentIds.length,
        errors: [copyErrorMessage(status, error)]
      };
    }

    // Parse edge function response
    if (!data) {
      log.error('Edge function returned no data');
      return {
        ...result,
        success: false,
        failedCount: request.attachmentIds.length,
        errors: ['No response from copy operation']
      };
    }

    // Map edge function response to our result format
    result.success = data.success ?? false;
    result.copiedCount = data.copiedCount ?? 0;
    result.failedCount = data.failedCount ?? 0;
    result.errors = data.errors ?? [];

    // Extract new attachment IDs from results
    if (data.results && Array.isArray(data.results)) {
      result.newAttachmentIds = data.results
        .filter((r: { success: boolean; newAttachmentId?: string }) => r.success && r.newAttachmentId)
        .map((r: { newAttachmentId: string }) => r.newAttachmentId);
    }

    log.info('Document copy to Encompass completed', {
      copiedCount: result.copiedCount,
      failedCount: result.failedCount,
      success: result.success
    });

    return result;

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unexpected error';
    log.error('Unexpected error during copy operation', { error });

    return {
      ...result,
      success: false,
      failedCount: request.attachmentIds.length,
      errors: [errorMessage]
    };
  }
}

/** supabase-js attaches the original Response on FunctionsHttpError.context. */
function readErrorStatus(error: unknown): number | undefined {
  const ctx = (error as { context?: unknown } | null)?.context;
  return ctx instanceof Response ? ctx.status : undefined;
}

/** Actionable copy-failure text. 546 = Supabase WORKER_LIMIT (resource kill);
 *  504 = gateway timeout — both read as "too large / took too long" to a user. */
function copyErrorMessage(status: number | undefined, error: unknown): string {
  if (status === 546 || status === 504) {
    return 'The copy service hit a resource limit on this file — it may be too large to copy in one request. Please try again, or contact support if it keeps failing.';
  }
  const base = error instanceof Error ? error.message : 'Edge function error';
  return status ? `Edge function error (${status}): ${base}` : `Edge function error: ${base}`;
}

/**
 * Gets a summary message for toast notification
 */
export function getCopyResultMessage(
  result: CopyDocumentsResult,
  borrowerName?: string
): string {
  if (result.success) {
    const destination = borrowerName ? ` to ${borrowerName}'s loan` : '';
    return `Copied ${result.copiedCount} document${result.copiedCount !== 1 ? 's' : ''}${destination}. They will appear in Encompass shortly.`;
  }

  if (result.copiedCount > 0) {
    return `Copied ${result.copiedCount} of ${result.copiedCount + result.failedCount} documents (${result.failedCount} failed)`;
  }

  return 'Failed to copy documents to Encompass';
}
